import React from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Pie } from 'react-chartjs-2'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { type EntityId, idKey } from '../types/constants'
import Profile = Models.Profile

ChartJS.register(ArcElement, Tooltip, Legend)

export const bgColors = [
    'rgba(255, 99, 132, 0.2)',
    'rgba(54, 162, 235, 0.2)',
    'rgba(255, 206, 86, 0.2)',
    'rgba(75, 192, 192, 0.2)',
    'rgba(153, 102, 255, 0.2)',
    'rgba(255, 159, 64, 0.2)',
]

export const borderColors = [
    'rgba(255, 99, 132, 1)',
    'rgba(54, 162, 235, 1)',
    'rgba(255, 206, 86, 1)',
    'rgba(75, 192, 192, 1)',
    'rgba(153, 102, 255, 1)',
    'rgba(255, 159, 64, 1)',
]

export interface PieChartDataGroup {
    converted: PieChartData
    current: PieChartData
    previous: PieChartData
    profile: Profile
}

interface PieChartData {
    acos: number
    aov: number
    cac: number
    clicks: number
    cost: number
    cpc: number
    ctr: number
    cvr: number
    date: string
    estimated_royalties: number
    impressions: number
    orders: number
    pages_read: number
    roas: number
    rpc: number
    sales: number
}

interface PieChartProps {
    data: PieChartDataGroup[]
    dataKey: keyof PieChartData
    label: string
    // MER-2802: keyed by profile_id, which may arrive as a number or a string.
    // A string index signature accepts both — JS coerces the key either way.
    visible: { [key: string]: boolean }
}

export const PieChart = ({ data, dataKey, label, visible }: PieChartProps) => {
    if (!data) {
        return null
    }

    const bgs: string[] = []
    const borders: string[] = []
    Object.keys(visible).forEach((profileId, i) => {
        if (visible[profileId]) {
            bgs.push(bgColors[i % bgColors.length])
            borders.push(borderColors[i % borderColors.length])
        }
    })

    const pieData = {
        labels: data
            .filter((i) => visible[i.profile.profile_id])
            .map((i) => i.profile.nickname || i.profile.name),
        datasets: [
            {
                label,
                data: data
                    .filter((i) => visible[i.profile.profile_id])
                    .map((i) => i.converted[dataKey]),
                backgroundColor: bgs,
                borderColor: borders,
                borderWidth: 1,
            },
        ],
    }

    return (
        <Pie
            data={pieData}
            options={{
                plugins: {
                    legend: {
                        display: false,
                    },
                },
            }}
            // options={{cutout:     '25%'}}
        />
    )
}

interface PieChartLegendProps {
    data: PieChartDataGroup[]
    className?: ClassValue
    // MER-2802: keyed by profile_id, which may arrive as a number or a string.
    visible: { [key: string]: boolean }
    onClick: (v: EntityId) => any
}

export const PieChartLegend = ({ data, className, visible, onClick }: PieChartLegendProps) => {
    if (!data) {
        return null
    }

    const activeCount = Object.keys(visible).reduce((p, k) => {
        return visible[k] ? p + 1 : p
    }, 0)

    return (
        <div className={classNames(className, 'flex flex-col items-start justify-center pt-6')}>
            {data
                .filter((i) => i.converted.cost > 0)
                .map((item) => {
                    // MER-2802: profile_id may be a number or a string. Compare on String(...).
                    const index = Object.keys(visible).findIndex(
                        (i) => i === idKey(item.profile.profile_id),
                    )

                    return (
                        <div
                            key={item.profile.profile_id}
                            className={classNames('flex mb-0.5 items-center', {
                                'text-gray-400': !visible[item.profile.profile_id],
                                'cursor-pointer':
                                    activeCount > 1 || !visible[item.profile.profile_id],
                            })}
                            onClick={() => {
                                if (activeCount != 1 || !visible[item.profile.profile_id]) {
                                    onClick(item.profile.profile_id)
                                }
                            }}
                        >
                            <div
                                className={classNames('w-4 h-4 mr-2', {
                                    'bg-gray-100 border-gray-300':
                                        !visible[item.profile.profile_id],
                                })}
                                style={{
                                    background: visible[item.profile.profile_id]
                                        ? bgColors[index % bgColors.length]
                                        : '',
                                    borderColor: visible[item.profile.profile_id]
                                        ? borderColors[index % borderColors.length]
                                        : '',
                                    borderWidth: 1,
                                }}
                            />
                            {item.profile.nickname || item.profile.name}
                        </div>
                    )
                })}
        </div>
    )
}
